import { useState, useEffect } from 'react';
import { useLocation, Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { NAV_LINKS } from '../constants';
import { recaptureGlass } from '../lib/glass';

export function Wordmark({ size = 'md', light = false }) {
  const img = size === 'lg' ? 'w-12 h-12' : 'w-9 h-9';
  return (
    <span className="flex items-center gap-2.5">
      <img
        src={light ? '/farwings-mark.png' : '/farwings-mark-dark.png'}
        alt=""
        width="40"
        height="40"
        className={`nav-bird ${img} object-contain select-none`}
      />
      <span className="display-semi text-[1.2rem] tracking-[-0.02em]">Farwings</span>
    </span>
  );
}

const LINKS = NAV_LINKS.filter((l) => l.href !== '/contact');


export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 30, restDelta: 0.001 });
  const [hidden, setHidden] = useState(false);

  // slide away while scrolling down, come back on any scroll up
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const dy = y - lastY;
        if (y < 120) setHidden(false);
        else if (dy > 6) setHidden(true);
        else if (dy < -6) setHidden(false);
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);


  useEffect(() => {
    setOpen(false);
    recaptureGlass(1100);
  }, [location.pathname]);

  useEffect(() => {
    const onLoad = () => recaptureGlass(300);
    window.addEventListener('load', onLoad);
    return () => window.removeEventListener('load', onLoad);
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (window.lenis) open ? window.lenis.stop() : window.lenis.start();
  }, [open]);

  const activeIndex = LINKS.findIndex((l) => location.pathname.startsWith(l.href));
  const lit = hover ?? (activeIndex >= 0 ? activeIndex : null);

  return (
    <>
      <header
        className={`pointer-events-none fixed inset-x-0 top-3 z-[60] flex justify-center px-3 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] sm:top-5 ${
          hidden && !open ? '-translate-y-[140%]' : 'translate-y-0'
        }`}
      >
        <div className="relative w-full max-w-[64rem]">
          <div
            data-glass="nav"
            onMouseMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
              e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
            }}
            className="glass-nav theme-dark pointer-events-auto relative z-[60] flex h-16 w-full items-center justify-between gap-3 rounded-full pl-4 pr-2 text-ink"
          >
            <Link to="/" aria-label="Farwings Tech Solution home" className="on-glass shrink-0">
              <Wordmark light />
            </Link>

            <nav aria-label="Main" className="on-glass hidden lg:block" onMouseLeave={() => setHover(null)}>
              <ul className="flex items-center">
                {LINKS.map((link, i) => (
                  <li key={link.href} onMouseEnter={() => setHover(i)}>
                    <NavLink
                      to={link.href}
                      className={({ isActive }) =>
                        `relative block rounded-full px-4 py-2.5 text-[0.9375rem] font-medium transition-colors ${
                          isActive || lit === i ? 'text-white' : 'text-white/85'
                        }`
                      }
                    >
                      {lit === i && (
                        <motion.span
                          layoutId="nav-bubble"
                          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                          className="absolute inset-0 rounded-full bg-white/[0.13] ring-1 ring-inset ring-white/15"
                        />
                      )}
                      <span className="relative">{link.name}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="on-glass flex items-center gap-2">
              <Link
                to="/contact"
                className="nav-cta group btn relative hidden h-11 overflow-hidden rounded-full bg-white px-5 text-xs uppercase tracking-[0.14em] text-[#07101F] hover:bg-[#4FB0E7] sm:inline-flex"
              >
                <span className="relative">Start a project</span>
                <span aria-hidden="true" className="relative -mr-1 w-0 overflow-hidden transition-[width] duration-300 group-hover:w-4">
                  &rarr;
                </span>
              </Link>
              <button
                type="button"
                className="relative flex h-11 w-11 items-center justify-center rounded-full bg-white/10 lg:hidden"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-controls="mobile-menu"
                aria-label={open ? 'Close menu' : 'Open menu'}
              >
                <span className={`absolute h-[1.5px] w-5 bg-white transition-transform duration-300 ${open ? 'rotate-45' : '-translate-y-1'}`} />
                <span className={`absolute h-[1.5px] w-5 bg-white transition-transform duration-300 ${open ? '-rotate-45' : 'translate-y-1'}`} />
              </button>
            </div>
          </div>

          {/* reading progress, drawn outside the glass so it doesn't force re-renders */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-10 bottom-[3px] z-[61] h-[2px] overflow-hidden rounded-full">
            <motion.div className="h-full origin-left rounded-full bg-[#4FB0E7]" style={{ scaleX: progress }} />
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            data-liquid-ignore
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="theme-dark sky-noon fixed inset-0 z-[55] pt-24 lg:hidden"
          >
            <nav aria-label="Mobile" className="frame flex h-full flex-col justify-between pb-10">
              <ul className="space-y-1">
                {[{ name: 'Home', href: '/' }, ...NAV_LINKS].map((link, i) => (
                  <motion.li
                    key={link.href}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={link.href}
                      onClick={() => setOpen(false)}
                      className="block display py-1.5 text-[2.75rem] text-white transition-colors hover:text-accent sm:text-6xl"
                    >
                      {link.name}
                    </Link>
                  </motion.li>
                ))}
              </ul>
              <div className="space-y-1 text-sm text-muted">
                <a href="tel:+917043031553" className="block text-white">
                  +91 70430 31553
                </a>
                <a href="tel:+918866823010" className="block text-white">
                  +91 88668 23010
                </a>
                <a href="mailto:farwingsolutions@gmail.com" className="block break-all">
                  farwingsolutions@gmail.com
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
